import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../lib/api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { Bookmark, ChevronDown, ChevronUp, Loader2, Search, Trash2, CheckCircle2, RotateCcw, Send, MessageSquare, ExternalLink, Calendar, User, IndianRupee, BookOpen, Briefcase, X, Filter, Pencil } from 'lucide-react';
import { BOOKMARK_CATEGORIES, BOOKMARK_PRIORITIES, normCats, catMap as importedCatMap } from '../../components/BookmarkButton';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { ZohoCustomerLink, ZohoInvoiceLink, ZohoCrmLeadLink } from '../../components/ZohoLinks';
import { toast } from 'sonner';
import MentionTextarea from '../../components/MentionTextarea';
import MentionText from '../../components/MentionText';

const fmt = (v) => {
  if (v == null || isNaN(v)) return '-';
  return Number(v).toLocaleString('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

const catMap = importedCatMap;
const prioMap = {};
BOOKMARK_PRIORITIES.forEach(p => { prioMap[p.value] = p; });

export default function AuditBookmarksPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [bookmarks, setBookmarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [detailData, setDetailData] = useState({});
  const [detailLoading, setDetailLoading] = useState({});
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilters, setCategoryFilters] = useState([]);
  const [search, setSearch] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [flashId, setFlashId] = useState(null);
  const rowRefs = useRef({});

  const toggleCatFilter = (val) => {
    setCategoryFilters(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);
  };

  const fetchBookmarks = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (statusFilter) params.append('status', statusFilter);
    if (categoryFilters.length > 0) params.append('category', categoryFilters.join(','));
    api.get(`/training/audit-bookmarks?${params.toString()}`)
      .then(r => setBookmarks(r.data.bookmarks || []))
      .catch(() => toast.error('Failed to load bookmarks'))
      .finally(() => setLoading(false));
  }, [statusFilter, categoryFilters]);

  useEffect(() => { fetchBookmarks(); }, [fetchBookmarks]);

  // Deep-link: ?focus_enroll=<enrollment_no> or ?focus_bookmark=<bookmark_id>
  // Notifications from the backend use the shorthand ?focus=<bookmark_id>
  // so accept that as an alias to match the "notification click opens the
  // specific item" behaviour.
  const focusEnroll = searchParams.get('focus_enroll');
  const focusBookmarkId = searchParams.get('focus_bookmark') || searchParams.get('focus');
  useEffect(() => {
    if (!focusEnroll && !focusBookmarkId) return;
    if (loading || bookmarks.length === 0) return;
    const target = bookmarks.find(b =>
      (focusBookmarkId && b.id === focusBookmarkId) ||
      (focusEnroll && String(b.enrollment_no) === String(focusEnroll))
    );
    if (!target) {
      // It might be filtered out — clear filters and retry
      if (statusFilter || categoryFilters.length > 0 || search) {
        setStatusFilter(''); setCategoryFilters([]); setSearch('');
        return;
      }
      toast.info(`No bookmark found for ${focusEnroll || focusBookmarkId}`);
      setSearchParams({}, { replace: true });
      return;
    }
    setExpandedId(target.id);
    setFlashId(target.id);
    // Trigger detail fetch via existing toggle path
    if (!detailData[target.id]) {
      api.get(`/training/audit-bookmarks/${target.id}`)
        .then(r => setDetailData(p => ({ ...p, [target.id]: r.data })))
        .catch(() => {});
    }
    setTimeout(() => {
      const el = rowRefs.current[target.id];
      if (el?.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 80);
    const t = setTimeout(() => setFlashId(null), 2500);
    setSearchParams({}, { replace: true });
    return () => clearTimeout(t);
  }, [focusEnroll, focusBookmarkId, bookmarks, loading, detailData, statusFilter, categoryFilters, search, setSearchParams]);

  const toggleExpand = async (bm) => {
    const id = bm.id;
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(id);
    if (!detailData[id]) {
      setDetailLoading(p => ({ ...p, [id]: true }));
      try {
        const r = await api.get(`/training/audit-bookmarks/${id}`);
        setDetailData(p => ({ ...p, [id]: r.data }));
      } catch {
        toast.error('Failed to load details');
      } finally {
        setDetailLoading(p => ({ ...p, [id]: false }));
      }
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/training/audit-bookmarks/${id}`, { status });
      toast.success(status === 'resolved' ? 'Marked as resolved' : 'Reopened');
      fetchBookmarks();
    } catch {
      toast.error('Failed to update');
    }
  };

  const removeBookmark = async (id) => {
    try {
      await api.delete(`/training/audit-bookmarks/${id}`);
      toast.success('Bookmark removed');
      setDeleteId(null);
      setExpandedId(null);
      fetchBookmarks();
    } catch {
      toast.error('Failed to remove');
    }
  };

  const filtered = bookmarks.filter(bm => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (bm.student_name || '').toLowerCase().includes(q) ||
      (bm.enrollment_no || '').toLowerCase().includes(q) ||
      (bm.issue_text || '').toLowerCase().includes(q);
  });

  const openCount = bookmarks.filter(b => b.status === 'open').length;
  const resolvedCount = bookmarks.filter(b => b.status === 'resolved').length;

  return (
    <div className="space-y-4" data-testid="audit-bookmarks-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <Bookmark className="w-5 h-5 text-amber-500" /> Audit Bookmarks
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">{openCount} open &middot; {resolvedCount} resolved</p>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search name, enrollment, issue..."
              className="h-8 text-xs pl-8"
              data-testid="bookmark-search"
            />
          </div>
          <Select value={statusFilter} onValueChange={v => setStatusFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="h-8 text-xs w-[130px]" data-testid="bookmark-status-filter">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex flex-wrap gap-1.5 items-center">
            {BOOKMARK_CATEGORIES.map(c => (
              <button
                key={c.value}
                onClick={() => toggleCatFilter(c.value)}
                className={`px-2 py-1 rounded text-[10px] font-medium border transition-all ${
                  categoryFilters.includes(c.value) ? c.color + ' ring-1 ring-offset-1 ring-slate-300' : 'bg-white text-slate-400 border-slate-200 hover:bg-slate-50'
                }`}
                data-testid={`filter-category-${c.value}`}
              >
                {c.label}
              </button>
            ))}
          </div>
          {(statusFilter || categoryFilters.length > 0 || search) && (
            <button onClick={() => { setStatusFilter(''); setCategoryFilters([]); setSearch(''); }} className="text-[10px] text-slate-400 hover:text-red-500">Clear all</button>
          )}
        </div>
      </Card>

      {/* Table */}
      <Card className="overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-slate-400 text-sm">
            {bookmarks.length === 0 ? 'No bookmarks yet. Use the bookmark icon on any student to add one.' : 'No bookmarks match your filters.'}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/80">
                <TableHead className="w-8" />
                <TableHead className="text-[10px] uppercase">Student</TableHead>
                <TableHead className="text-[10px] uppercase">Enrollment</TableHead>
                <TableHead className="text-[10px] uppercase">Category</TableHead>
                <TableHead className="text-[10px] uppercase">Priority</TableHead>
                <TableHead className="text-[10px] uppercase text-right">Due Amt</TableHead>
                <TableHead className="text-[10px] uppercase">Status</TableHead>
                <TableHead className="text-[10px] uppercase">Resolved By</TableHead>
                <TableHead className="text-[10px] uppercase">Resolved Date</TableHead>
                <TableHead className="text-[10px] uppercase">Bookmarked By</TableHead>
                <TableHead className="text-[10px] uppercase">Date</TableHead>
                <TableHead className="text-[10px] uppercase text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(bm => (
                <BookmarkRow
                  key={bm.id}
                  bm={bm}
                  expanded={expandedId === bm.id}
                  flashing={flashId === bm.id}
                  rowRef={el => { if (el) rowRefs.current[bm.id] = el; }}
                  onToggle={() => toggleExpand(bm)}
                  detail={detailData[bm.id]}
                  detailLoading={detailLoading[bm.id]}
                  onResolve={() => updateStatus(bm.id, 'resolved')}
                  onReopen={() => updateStatus(bm.id, 'open')}
                  onDelete={() => setDeleteId(bm.id)}
                  onDetailUpdate={(d) => setDetailData(p => ({ ...p, [bm.id]: d }))}
                  onCategoryChange={fetchBookmarks}
                />
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Bookmark?</AlertDialogTitle>
            <AlertDialogDescription>This will permanently remove this audit bookmark and all its comments.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="text-xs">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700 text-xs" data-testid="confirm-remove-bookmark" onClick={() => removeBookmark(deleteId)}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function BookmarkRow({ bm, expanded, flashing, rowRef, onToggle, detail, detailLoading, onResolve, onReopen, onDelete, onDetailUpdate, onCategoryChange }) {
  const cats = normCats(bm.category);
  const prio = prioMap[bm.priority] || prioMap.medium;
  const sd = bm.student_data || {};

  return (
    <>
      <TableRow
        ref={rowRef}
        className={`hover:bg-amber-50/30 cursor-pointer ${flashing ? 'ring-2 ring-violet-400 ring-offset-1 bg-violet-100/60 transition-all duration-700' : 'transition-all duration-700'}`}
        onClick={onToggle}
        data-testid={`bookmark-row-${bm.id}`}
      >
        <TableCell className="text-center">{expanded ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}</TableCell>
        <TableCell>
          <div className="text-sm font-medium">{bm.student_name || sd.name || '-'}</div>
          <div className="text-[10px] text-slate-400">{bm.course || sd.course || ''}</div>
        </TableCell>
        <TableCell className="text-xs">
          <ZohoCustomerLink enrollmentNo={bm.enrollment_no} zohoCustomerId={sd.zoho_customer_id} />
        </TableCell>
        <TableCell onClick={e => e.stopPropagation()}>
          <CategoryEditor bookmarkId={bm.id} currentCategories={cats} onChanged={onCategoryChange} />
        </TableCell>
        <TableCell>
          <Badge variant="outline" className={`text-[10px] ${prio.color}`}>{prio.label}</Badge>
        </TableCell>
        <TableCell className="text-xs text-right font-medium text-red-500">{sd.due_amount > 0 ? fmt(sd.due_amount) : fmt(0)}</TableCell>
        <TableCell>
          <Badge variant="outline" className={`text-[10px] ${bm.status === 'open' ? 'bg-orange-50 text-orange-700 border-orange-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}>
            {bm.status === 'open' ? 'Open' : 'Resolved'}
          </Badge>
        </TableCell>
        <TableCell className="text-[10px] text-slate-600">
          {bm.status === 'resolved' ? (bm.resolved_by || '-') : <span className="text-slate-300">-</span>}
        </TableCell>
        <TableCell className="text-[10px] text-slate-500">
          {bm.status === 'resolved' && bm.resolved_at
            ? new Date(bm.resolved_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })
            : <span className="text-slate-300">-</span>}
        </TableCell>
        <TableCell className="text-[10px] text-slate-600">
          {bm.created_by || '-'}
        </TableCell>
        <TableCell className="text-[10px] text-slate-500">
          {new Date(bm.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })}
        </TableCell>
        <TableCell className="text-right" onClick={e => e.stopPropagation()}>
          <div className="flex gap-1 justify-end">
            {bm.status === 'open' ? (
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Mark Resolved" onClick={onResolve} data-testid={`resolve-bookmark-${bm.id}`}>
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
              </Button>
            ) : (
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Reopen" onClick={onReopen} data-testid={`reopen-bookmark-${bm.id}`}>
                <RotateCcw className="w-3.5 h-3.5 text-amber-500" />
              </Button>
            )}
            {bm.status === 'open' && (
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Remove" onClick={onDelete} data-testid={`delete-bookmark-${bm.id}`}>
                <Trash2 className="w-3.5 h-3.5 text-red-400" />
              </Button>
            )}
          </div>
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={12} className="bg-amber-50/20 p-0">
            {detailLoading ? (
              <div className="flex items-center justify-center py-8"><Loader2 className="w-4 h-4 animate-spin text-slate-400" /></div>
            ) : detail ? (
              <BookmarkDetail detail={detail} onDetailUpdate={onDetailUpdate} />
            ) : (
              <div className="text-center py-8 text-xs text-slate-400">Failed to load details</div>
            )}
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function BookmarkDetail({ detail, onDetailUpdate }) {
  const [activeTab, setActiveTab] = useState('overview');
  const sd = detail.student_data || {};

  const tabs = [
    { id: 'overview', label: 'Overview', icon: User },
    { id: 'fees', label: 'Fees/Invoice', icon: IndianRupee },
    { id: 'attendance', label: 'Attendance', icon: Calendar },
    { id: 'batches', label: 'Batch Info', icon: BookOpen },
  ];

  return (
    <div className="p-4 space-y-4" data-testid={`bookmark-detail-${detail.id}`}>
      {/* Issue banner */}
      <div className="bg-white border border-amber-200 rounded-md p-3">
        <div className="flex items-center gap-2 mb-1">
          <p className="text-[10px] uppercase tracking-wider text-amber-600 font-semibold">Audit Issue</p>
          <div className="flex gap-1">
            {normCats(detail.category).map(cv => {
              const cm = catMap[cv] || catMap.fees_invoice;
              return <Badge key={cv} variant="outline" className={`text-[8px] ${cm.color}`}>{cm.label}</Badge>;
            })}
          </div>
        </div>
        <p className="text-sm text-slate-700"><MentionText text={detail.issue_text || '-'} /></p>
        <div className="flex items-center gap-3 mt-2.5 pt-2.5 border-t border-dashed border-amber-200 text-xs text-slate-500">
          <User className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
          <span>Bookmarked by <span className="text-slate-800 font-semibold">{detail.created_by || '-'}</span>
            {detail.created_by_email && <span className="text-slate-400 ml-1">({detail.created_by_email})</span>}
          </span>
          <span className="text-slate-300">&middot;</span>
          <span>{detail.created_at ? new Date(detail.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
              activeTab === t.id ? 'border-amber-500 text-amber-700' : 'border-transparent text-slate-400 hover:text-slate-600'
            }`}
            data-testid={`bookmark-tab-${t.id}`}
          >
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="min-h-[100px]">
        {activeTab === 'overview' && <OverviewTab sd={sd} detail={detail} />}
        {activeTab === 'fees' && <FeesTab sd={sd} invoices={detail.invoices || []} />}
        {activeTab === 'attendance' && <AttendanceTab attendance={detail.attendance || []} />}
        {activeTab === 'batches' && <BatchesTab batches={detail.batches || []} />}
      </div>

      {/* Comments */}
      <CommentsSection detail={detail} onDetailUpdate={onDetailUpdate} />
    </div>
  );
}

function OverviewTab({ sd, detail }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3 text-sm">
      <div><span className="text-slate-400 text-xs">Name</span><div className="font-medium">{sd.name || detail.student_name || '-'}</div></div>
      <div><span className="text-slate-400 text-xs">Enrollment</span><div className="font-mono font-medium text-violet-600">{detail.enrollment_no}</div></div>
      <div><span className="text-slate-400 text-xs">Course</span><div className="font-medium">{sd.course || detail.course || '-'}</div></div>
      <div><span className="text-slate-400 text-xs">Branch</span><div>{sd.branch || '-'}</div></div>
      <div><span className="text-slate-400 text-xs">Counsellor</span><div>{sd.salesperson || '-'}</div></div>
      <div><span className="text-slate-400 text-xs">Phone</span><div>{sd.phone || '-'}</div></div>
      <div><span className="text-slate-400 text-xs">Email</span><div className="truncate">{sd.email || '-'}</div></div>
      <div>
        <span className="text-slate-400 text-xs">Zoho Links</span>
        <div className="flex gap-2 mt-0.5 flex-wrap">
          <ZohoCustomerLink enrollmentNo="View in Books" zohoCustomerId={sd.zoho_customer_id} className="text-xs" />
          <ZohoCrmLeadLink enrollmentNo={detail.enrollment_no} compact />
        </div>
      </div>
      <div>
        <span className="text-slate-400 text-xs">Financial</span>
        <div className="text-xs mt-0.5">
          Total: <span className="font-medium">{fmt(sd.total_amount)}</span> &middot;
          Paid: <span className="text-emerald-600">{fmt(sd.paid_amount)}</span> &middot;
          Due: <span className="text-red-500 font-medium">{fmt(sd.due_amount)}</span>
        </div>
      </div>
    </div>
  );
}

function FeesTab({ sd, invoices }) {
  if (!invoices || invoices.length === 0) {
    return <p className="text-xs text-slate-400 italic py-4">No invoice data available</p>;
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-slate-50 rounded-md p-2.5 text-center">
          <div className="text-[10px] text-slate-400 uppercase">Total</div>
          <div className="text-sm font-bold">{fmt(sd.total_amount)}</div>
        </div>
        <div className="bg-emerald-50 rounded-md p-2.5 text-center">
          <div className="text-[10px] text-emerald-600 uppercase">Paid</div>
          <div className="text-sm font-bold text-emerald-700">{fmt(sd.paid_amount)}</div>
        </div>
        <div className="bg-red-50 rounded-md p-2.5 text-center">
          <div className="text-[10px] text-red-500 uppercase">Due</div>
          <div className="text-sm font-bold text-red-600">{fmt(sd.due_amount)}</div>
        </div>
        <div className="bg-amber-50 rounded-md p-2.5 text-center">
          <div className="text-[10px] text-amber-600 uppercase">Write-off</div>
          <div className="text-sm font-bold text-amber-700">{fmt(sd.write_off_amount)}</div>
        </div>
      </div>

      <div className="border rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-[9px] uppercase">Invoice</TableHead>
              <TableHead className="text-[9px] uppercase">Date</TableHead>
              <TableHead className="text-[9px] uppercase text-right">Total</TableHead>
              <TableHead className="text-[9px] uppercase text-right">Paid</TableHead>
              <TableHead className="text-[9px] uppercase text-right">Balance</TableHead>
              <TableHead className="text-[9px] uppercase">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoices.map((inv, i) => (
              <React.Fragment key={i}>
                <TableRow>
                  <TableCell className="text-xs"><ZohoInvoiceLink invoiceNumber={inv.invoice_number || inv.invoice_id} invoiceId={inv.invoice_id} /></TableCell>
                  <TableCell className="text-xs text-slate-500">{inv.date || '-'}</TableCell>
                  <TableCell className="text-xs text-right">{fmt(inv.total)}</TableCell>
                  <TableCell className="text-xs text-right text-emerald-600">{fmt(inv.payment_made)}</TableCell>
                  <TableCell className="text-xs text-right text-red-500 font-medium">{fmt(inv.balance)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`text-[9px] ${
                      inv.status === 'paid' ? 'bg-emerald-50 text-emerald-700' :
                      inv.status === 'partially_paid' ? 'bg-amber-50 text-amber-700' :
                      inv.status === 'overdue' ? 'bg-red-50 text-red-600' : 'bg-slate-50 text-slate-500'
                    }`}>
                      {(inv.status || '').replace('_', ' ')}
                    </Badge>
                  </TableCell>
                </TableRow>
                {inv.line_items && inv.line_items.length > 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="pt-0 pb-2 px-4">
                      <div className="bg-slate-50 rounded-sm border border-dashed border-slate-200 px-3 py-1.5">
                        <p className="text-[9px] uppercase text-slate-400 font-semibold mb-1">Line Items</p>
                        {inv.line_items.map((li, j) => (
                          <div key={j} className="flex items-center justify-between text-[11px]">
                            <span className="text-slate-700 truncate">{li.name}</span>
                            <div className="flex gap-3 ml-3 text-slate-500">
                              <span>Qty: {li.quantity || 1}</span>
                              <span>Rate: {fmt(li.rate)}</span>
                              <span className="font-medium text-slate-700">{fmt(li.item_total)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function AttendanceTab({ attendance }) {
  if (!attendance || attendance.length === 0) {
    return (
      <div className="text-center py-6">
        <Calendar className="w-8 h-8 text-slate-200 mx-auto mb-2" />
        <p className="text-xs text-slate-400">No attendance records found for this student</p>
      </div>
    );
  }

  const presentCount = attendance.filter(a => a.status === 'present').length;
  const totalCount = attendance.length;
  const rate = totalCount > 0 ? Math.round((presentCount / totalCount) * 100) : 0;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-4">
        <div className="bg-slate-50 rounded-md px-3 py-2">
          <span className="text-[10px] text-slate-400 uppercase">Sessions</span>
          <div className="text-sm font-bold">{totalCount}</div>
        </div>
        <div className="bg-emerald-50 rounded-md px-3 py-2">
          <span className="text-[10px] text-emerald-600 uppercase">Present</span>
          <div className="text-sm font-bold text-emerald-700">{presentCount}</div>
        </div>
        <div className="bg-slate-50 rounded-md px-3 py-2">
          <span className="text-[10px] text-slate-400 uppercase">Rate</span>
          <div className={`text-sm font-bold ${rate >= 75 ? 'text-emerald-600' : rate >= 50 ? 'text-amber-600' : 'text-red-500'}`}>{rate}%</div>
        </div>
        {totalCount <= 3 && (
          <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">Low attendance data</Badge>
        )}
      </div>

      <div className="border rounded-md overflow-hidden max-h-[250px] overflow-y-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 sticky top-0">
              <TableHead className="text-[9px] uppercase">Date</TableHead>
              <TableHead className="text-[9px] uppercase">Status</TableHead>
              <TableHead className="text-[9px] uppercase">Mode</TableHead>
              <TableHead className="text-[9px] uppercase">Trainer</TableHead>
              <TableHead className="text-[9px] uppercase">Completion</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {attendance.map((a, i) => (
              <TableRow key={i}>
                <TableCell className="text-xs">{a.date ? new Date(a.date + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '-'}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={`text-[9px] ${a.status === 'present' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>
                    {a.status || '-'}
                  </Badge>
                </TableCell>
                <TableCell className="text-xs text-slate-500">{a.mode_of_training || '-'}</TableCell>
                <TableCell className="text-xs text-slate-500">{a.trainer_name || '-'}</TableCell>
                <TableCell className="text-xs">{a.completion_percentage != null ? `${a.completion_percentage}%` : '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function BatchesTab({ batches }) {
  if (!batches || batches.length === 0) {
    return (
      <div className="text-center py-6">
        <BookOpen className="w-8 h-8 text-slate-200 mx-auto mb-2" />
        <p className="text-xs text-slate-400">No batch information found</p>
      </div>
    );
  }

  const isCourseMatch = batches.some(b => b.is_course_match);

  return (
    <div className="space-y-2">
      {isCourseMatch && (
        <div className="bg-blue-50 border border-blue-200 rounded-md px-3 py-2 text-xs text-blue-700">
          Student is not formally enrolled in a batch. Showing running/upcoming batches for the same course.
        </div>
      )}
      {batches.map((b, i) => (
        <div key={i} className="bg-white border rounded-md p-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium">{b.batch_name}</span>
            {b.batch_number && <Badge variant="outline" className="text-[9px] bg-violet-50 text-violet-700">#{b.batch_number}</Badge>}
            <Badge variant="outline" className={`text-[9px] ${
              b.batch_status === 'Running' ? 'bg-emerald-50 text-emerald-700' :
              b.batch_status === 'Completed' ? 'bg-slate-50 text-slate-500' :
              b.batch_status === 'Upcoming' ? 'bg-blue-50 text-blue-600' : 'bg-amber-50 text-amber-600'
            }`}>{b.batch_status || '-'}</Badge>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2 text-[11px] text-slate-500">
            <div>Course: <span className="text-slate-700">{b.course_name || '-'}</span></div>
            <div>Centre: <span className="text-slate-700">{b.centre_name || '-'}</span></div>
            <div>Trainer: <span className="text-slate-700">{b.trainer_name || '-'}</span></div>
            <div>Start: <span className="text-slate-700">{b.start_date || '-'}</span></div>
          </div>
          {b.zoho_batch_id && (
            <a
              href={`https://crm.zoho.com/crm/org862409702/tab/CustomModule1/${b.zoho_batch_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[10px] text-blue-600 hover:underline mt-1.5"
            >
              View in Zoho CRM <ExternalLink className="w-2.5 h-2.5" />
            </a>
          )}
        </div>
      ))}
    </div>
  );
}

function CommentsSection({ detail, onDetailUpdate }) {
  const [newComment, setNewComment] = useState('');
  const [mentionedIds, setMentionedIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const comments = detail.comments || [];

  const addComment = async () => {
    if (!newComment.trim()) return;
    setSubmitting(true);
    try {
      const res = await api.post(`/training/audit-bookmarks/${detail.id}/comments`, {
        text: newComment.trim(),
        mentioned_user_ids: mentionedIds,
      });
      const updated = { ...detail, comments: [res.data, ...comments] };
      onDetailUpdate(updated);
      setNewComment('');
      setMentionedIds([]);
      toast.success(mentionedIds.length > 0
        ? `Comment added & ${mentionedIds.length} teammate${mentionedIds.length > 1 ? 's' : ''} notified`
        : 'Comment added');
    } catch {
      toast.error('Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const deleteComment = async (commentId) => {
    try {
      await api.delete(`/training/audit-bookmarks/${detail.id}/comments/${commentId}`);
      const updated = { ...detail, comments: comments.filter(c => c.id !== commentId) };
      onDetailUpdate(updated);
      toast.success('Comment deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="border-t pt-3">
      <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2 flex items-center gap-1">
        <MessageSquare className="w-3 h-3" /> Comments ({comments.length})
      </p>
      <div className="flex gap-2 mb-2">
        <div className="flex-1">
          <MentionTextarea
            value={newComment}
            onChange={setNewComment}
            onMentionsChange={setMentionedIds}
            placeholder="Add a comment, type @ to mention a teammate..."
            rows={1}
            className="text-xs min-h-[36px] h-9 resize-none"
            data-testid={`bookmark-comment-input-${detail.id}`}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); addComment(); } }}
          />
          {mentionedIds.length > 0 && (
            <div className="mt-1 text-[10px] text-violet-600 flex items-center gap-1">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-violet-500" />
              {mentionedIds.length} teammate{mentionedIds.length > 1 ? 's' : ''} will be notified
            </div>
          )}
        </div>
        <Button size="sm" onClick={addComment} disabled={submitting || !newComment.trim()} className="h-9 text-xs" data-testid={`bookmark-comment-submit-${detail.id}`}>
          <Send className="w-3.5 h-3.5" />
        </Button>
      </div>
      {comments.length > 0 && (
        <div className="space-y-1.5 max-h-[150px] overflow-y-auto">
          {comments.map(c => (
            <div key={c.id} className="flex items-start gap-2 text-xs bg-white border rounded-sm px-2.5 py-1.5 group">
              <div className="flex-1">
                <span className="font-medium text-slate-700">{c.created_by}</span>
                <span className="text-slate-300 mx-1.5">&middot;</span>
                <span className="text-slate-400">{new Date(c.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                <p className="text-slate-600 mt-0.5"><MentionText text={c.text} /></p>
              </div>
              <button onClick={() => deleteComment(c.id)} className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-red-400 p-0.5" data-testid={`delete-comment-${c.id}`}>
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function CategoryEditor({ bookmarkId, currentCategories, onChanged }) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(currentCategories || []);
  const [saving, setSaving] = useState(false);

  const toggle = (val) => {
    setSelected(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);
  };

  const handleSave = async () => {
    if (selected.length === 0) { toast.error('Select at least one category'); return; }
    setSaving(true);
    try {
      await api.put(`/training/audit-bookmarks/${bookmarkId}`, { category: selected });
      toast.success('Categories updated');
      setOpen(false);
      if (onChanged) onChanged();
    } catch {
      toast.error('Failed to update');
    } finally {
      setSaving(false);
    }
  };

  // Sync when prop changes
  useEffect(() => { setSelected(currentCategories || []); }, [currentCategories]);

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v) setSelected(currentCategories || []); }}>
      <PopoverTrigger asChild>
        <button className="inline-flex items-center gap-1 flex-wrap" data-testid={`edit-category-${bookmarkId}`}>
          {(currentCategories || []).map(cv => {
            const cm = catMap[cv] || catMap.fees_invoice;
            return <span key={cv} className={`inline-flex items-center px-2 py-0.5 rounded border text-[10px] font-medium ${cm.color}`}>{cm.label}</span>;
          })}
          <Pencil className="w-2.5 h-2.5 text-slate-300 ml-0.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-60 p-2.5" align="start">
        <p className="text-[9px] uppercase text-slate-400 font-semibold mb-2 px-1">Edit Categories</p>
        <div className="space-y-0.5">
          {BOOKMARK_CATEGORIES.map(c => {
            const checked = selected.includes(c.value);
            return (
              <label
                key={c.value}
                className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer transition-colors ${checked ? 'bg-slate-50' : 'hover:bg-slate-50/60'}`}
                data-testid={`set-category-${c.value}`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(c.value)}
                  className="w-3.5 h-3.5 rounded border-slate-300 text-slate-700 focus:ring-slate-400 focus:ring-offset-0 cursor-pointer"
                />
                <span className={`w-2 h-2 rounded-full ${c.dot} flex-shrink-0`} />
                <span className="text-xs text-slate-700 select-none">{c.label}</span>
              </label>
            );
          })}
        </div>
        <Button size="sm" onClick={handleSave} disabled={saving || selected.length === 0} className="w-full mt-2 h-7 text-[10px] rounded-sm bg-slate-900 hover:bg-slate-800">
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </PopoverContent>
    </Popover>
  );
}
